import { useQuery } from '@tanstack/react-query';
import { useAuthContext } from '@/modules/auth/ui/AuthProvider';
import { queryKeys } from './queryKeys';

export function useAdminStats() {
  const { authFetch } = useAuthContext();

  return useQuery({
    queryKey: queryKeys.admin.stats,
    queryFn: async () => {
      const response = await authFetch('/api/admin/stats');
      if (!response.ok) throw new Error('Failed to load admin stats');
      return response.json();
    },
  });
}

export function useAdminApp(appId) {
  const { authFetch } = useAuthContext();

  return useQuery({
    queryKey: queryKeys.admin.app(appId),
    queryFn: async () => {
      const response = await authFetch(`/api/admin/app?appId=${appId}`);
      if (!response.ok) throw new Error('Failed to load app');
      const data = await response.json();
      if (!data.notes) {
        data.notes = [];
      }
      return data;
    },
    enabled: !!appId,
  });
}

export function useAdminQueue(page = 1, limit = 50) {
  const { authFetch } = useAuthContext();

  return useQuery({
    queryKey: [...queryKeys.admin.queue, page, limit],
    queryFn: async () => {
      const response = await authFetch(`/api/admin/queue?page=${page}&limit=${limit}`);
      if (!response.ok) throw new Error('Failed to load build queue');
      return response.json();
    },
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });
}

export function useQuotesNeeded(page = 1, limit = 50) {
  const { authFetch } = useAuthContext();

  return useQuery({
    queryKey: [...queryKeys.admin.quotesNeeded, page, limit],
    queryFn: async () => {
      const response = await authFetch(`/api/admin/quotes-needed?page=${page}&limit=${limit}`);
      if (!response.ok) throw new Error('Failed to load apps needing quotes');
      return response.json();
    },
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });
}

export function useReadyToLive(page = 1, limit = 50) {
  const { authFetch } = useAuthContext();

  return useQuery({
    queryKey: [...queryKeys.admin.readyToLive, page, limit],
    queryFn: async () => {
      const response = await authFetch(`/api/admin/ready-to-live?page=${page}&limit=${limit}`);
      if (!response.ok) throw new Error('Failed to load apps ready to go live');
      return response.json();
    },
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });
}

export function usePaymentIssues(page = 1, limit = 50) {
  const { authFetch } = useAuthContext();

  return useQuery({
    queryKey: [...queryKeys.admin.paymentIssues, page, limit],
    queryFn: async () => {
      const response = await authFetch(`/api/admin/payment-issues?page=${page}&limit=${limit}`);
      if (!response.ok) throw new Error('Failed to load apps with payment issues');
      return response.json();
    },
    refetchInterval: 60000, // Auto-refresh every 60 seconds
  });
}

export function useAppsNoPaymentMethod(page = 1, limit = 50) {
  const { authFetch } = useAuthContext();

  return useQuery({
    queryKey: [...queryKeys.admin.appsNoPayment, page, limit],
    queryFn: async () => {
      const response = await authFetch(`/api/admin/apps-no-payment-method?page=${page}&limit=${limit}`);
      if (!response.ok) throw new Error('Failed to load apps without payment method');
      return response.json();
    },
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });
}

export function useAdminApps(page = 1, limit = 50, filter = null, search = null) {
  const { authFetch } = useAuthContext();

  return useQuery({
    queryKey: [...queryKeys.admin.apps, page, limit, filter, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (filter && filter !== 'all') {
        params.append('filter', filter);
      }
      if (search) {
        params.append('search', search);
      }
      const response = await authFetch(`/api/admin/apps?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to load apps');
      return response.json();
    },
  });
}

export function useAdminErrors(page = 1, limit = 50) {
  const { authFetch } = useAuthContext();

  return useQuery({
    queryKey: [...queryKeys.admin.errors, page, limit],
    queryFn: async () => {
      const response = await authFetch(`/api/admin/errors?page=${page}&limit=${limit}`);
      if (!response.ok) throw new Error('Failed to load errors');
      return response.json();
    },
  });
}

export function useAdminError(errorId) {
  const { authFetch } = useAuthContext();

  return useQuery({
    queryKey: queryKeys.admin.error(errorId),
    queryFn: async () => {
      const response = await authFetch(`/api/admin/errors/${errorId}`);
      if (!response.ok) throw new Error('Failed to load error');
      return response.json();
    },
    enabled: !!errorId,
  });
}

export function useEmailTemplates() {
  const { authFetch } = useAuthContext();

  return useQuery({
    queryKey: queryKeys.admin.emailTemplates,
    queryFn: async () => {
      const response = await authFetch('/api/admin/email-templates');
      if (!response.ok) throw new Error('Failed to load email templates');
      const data = await response.json();
      return data.templates || [];
    },
  });
}

export function useAppServices(appId) {
  const { authFetch } = useAuthContext();

  return useQuery({
    queryKey: ['admin', 'services', appId],
    queryFn: async () => {
      const response = await authFetch(`/api/admin/services?appId=${appId}`);
      if (!response.ok) throw new Error('Failed to load services');
      return response.json();
    },
    enabled: !!appId,
  });
}
